import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { TranslocoService } from '@ngneat/transloco';
import { MatProgressButtonOptions } from 'mat-progress-buttons';
import { untilDestroyed } from 'ngx-take-until-destroy';
import { of, TimeoutError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Patient } from '../../../core/api/patient.service';
import {
  PatientFormControlType,
  PatientFormGroup
} from '../../../shared/patient-form/patient-form.model';
import { CSVPatientsFormGroup } from '../import-wizard-file-received/csv-patients-form-group.model';
import { MassClinicService } from './../../../mass-clinics/mass-clinic.service';
import {
  CreatePatientsResult,
  ImportWizardCheckExistingPatientsService
} from './import-wizard-check-existing-patients.service';

@Component({
  selector: 'csi-import-wizard-check-existing-patients',
  templateUrl: './import-wizard-check-existing-patients.component.html',
  styleUrls: ['./import-wizard-check-existing-patients.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImportWizardCheckExistingPatientsComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isMassClinic: boolean;
  @Input() csvPatientsFormGroup: CSVPatientsFormGroup;
  @Output() cancel: EventEmitter<null> = new EventEmitter();
  @Output() patientsCreated: EventEmitter<null> = new EventEmitter();
  @Output() goBack: EventEmitter<null> = new EventEmitter();
  public matSpinnerOptions: MatProgressButtonOptions;
  protected existingPatients: boolean[] = [];
  public awsError: string;
  // True when the existing-patient lookup could not complete (fetch error or timeout).
  // While this is true the Create action stays blocked, because we cannot tell which import
  // rows already exist — creating them anyway would duplicate existing patients.
  public dedupeFailed = false;
  // Set after the create phase when one or more rows could not be created. Holds a human summary
  // ("3 of 5 created. 2 failed: ... — reason"). While set, the wizard stays on this step instead
  // of advancing, so failed patients are never lost silently.
  public importFailureSummary: string = null;
  public patients: Patient[] = [];
  public createProcessed = 0;
  public createTotal = 0;
  public isDedupeRunning = false;
  public isCreateRunning = false;
  protected columnNamesMap = {
    firstName: 'First Name',
    lastName: 'Last Name',
    dateOfBirth: 'Date of Birth',
    gender: 'Gender',
    healthCard: 'Health Card',
    phone: 'Phone',
    email: 'Email',
    address: 'Address',
    action: ''
  };

  protected get displayableControls(): string[] {
    return this.importWizardCheckExistingPatientsService
      .reduceToUniqueKeys(this.patients)
      .filter(
        attribute =>
          this.patients.every(patient => patient[attribute] !== '') &&
          !!this.columnNamesMap[attribute as PatientFormControlType]
      );
  }

  protected get columns(): string[] {
    return this.csvPatientsFormGroup.hasExistingPatients
      ? [...this.displayableControls, 'action']
      : this.displayableControls;
  }

  constructor(
    private importWizardCheckExistingPatientsService: ImportWizardCheckExistingPatientsService,
    private changeDetectorRef: ChangeDetectorRef,
    private massClinicService: MassClinicService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit() {
    this.patients = [...(this.csvPatientsFormGroup.controls.patients.value as Patient[])];
    this.matSpinnerOptions = {
      active: true,
      text: '',
      raised: true,
      buttonColor: 'primary',
      spinnerSize: 19,
      mode: 'indeterminate'
    };
    this.importWizardCheckExistingPatientsService.createProgress$
      .pipe(untilDestroyed(this))
      .subscribe(progress => {
        this.createProcessed = progress.processed;
        this.createTotal = progress.total;
        this.changeDetectorRef.markForCheck();
      });
    this.isDedupeRunning = true;
    this.importWizardCheckExistingPatientsService
      .getAndPatchDuplicates(
        this.csvPatientsFormGroup.controls.patients.controls as PatientFormGroup[]
      )
      .pipe(
        untilDestroyed(this),
        catchError(dedupeError => {
          this.onDedupeFailed(dedupeError);
          return of(null);
        })
      )
      .subscribe(existingPatients => {
        this.isDedupeRunning = false;
        this.matSpinnerOptions.active = false;
        if (existingPatients === null) {
          // Dedupe did not complete. Leave doesPatientExist unset so the Create action stays
          // blocked — we must not create patients we were unable to check against the database.
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.dedupeFailed = false;
        this.setExistingPatients(existingPatients);
        this.updateButtonText();
      });
  }

  ngOnDestroy() {}

  ngOnChanges(changes: SimpleChanges) {
    if (changes['csvPatientsFormGroup']) {
      this.csvPatientsFormGroup.controls.shouldCreateDuplicates.valueChanges.subscribe(() =>
        this.updateButtonText()
      );
    }
  }

  private onDedupeFailed(dedupeError: unknown) {
    this.dedupeFailed = true;
    const wasTimeout = dedupeError instanceof TimeoutError;
    this.awsError = wasTimeout
      ? this.translocoService.translate(
          'Could not verify these patients against your existing records in time. Please retry, or import a smaller batch.'
        )
      : this.translocoService.translate(
          'Could not verify these patients against your existing records. Please retry.'
        );
    this.changeDetectorRef.markForCheck();
  }

  protected createPatients() {
    this.matSpinnerOptions.active = true;
    this.isCreateRunning = true;
    this.csvPatientsFormGroup.controls.shouldCreateDuplicates.disable();
    const patientsToCreate = this.csvPatientsFormGroup.patientsToCreate;
    this.importWizardCheckExistingPatientsService
      .createAndPatchNewPatients(patientsToCreate)
      .pipe(untilDestroyed(this))
      .subscribe((result: CreatePatientsResult) => {
        this.isCreateRunning = false;
        this.csvPatientsFormGroup.controls.shouldCreateDuplicates.enable();
        this.csvPatientsFormGroup.updateThisAndDescendants();
        if (result.failures.length > 0) {
          // Some rows could not be created. Do NOT advance as if everything succeeded — show
          // exactly which patients failed and why, and keep the user here. The patients that WERE
          // created are already persisted; re-importing the file skips them via the dedupe step.
          this.importFailureSummary = this.buildImportFailureSummary(result);
          this.matSpinnerOptions.active = false;
          this.changeDetectorRef.markForCheck();
          return;
        }
        this.importFailureSummary = null;
        setTimeout(() => this.patientsCreated.emit(null));
      });
  }

  private buildImportFailureSummary(result: CreatePatientsResult): string {
    const createdCount = result.created.length;
    const totalCount = createdCount + result.failures.length;
    const failedNames = result.failures.map(failure => failure.name).join(', ');
    const firstReason = result.failures[0] ? result.failures[0].reason : '';
    return (
      createdCount +
      ' ' +
      this.translocoService.translate('of') +
      ' ' +
      totalCount +
      ' ' +
      this.translocoService.translate('patients were created') +
      '. ' +
      result.failures.length +
      ' ' +
      this.translocoService.translate('could not be created') +
      ': ' +
      failedNames +
      (firstReason ? ' — ' + firstReason : '') +
      '. ' +
      this.translocoService.translate(
        'Re-import the file to retry — already-created patients are skipped automatically'
      ) +
      '.'
    );
  }

  protected editPatient(patientIndex: number) {
    this.massClinicService
      .openPatientModal(
        this.csvPatientsFormGroup.controls.patients.controls[patientIndex].value,
        false
      )
      .afterClosed()
      .subscribe((modifiedPatient: Patient) => {
        if (modifiedPatient) {
          this.csvPatientsFormGroup.controls.patients.controls[patientIndex].patchValue(
            modifiedPatient
          );
          this.patients[patientIndex] = modifiedPatient;
          this.patients = [...this.patients];
          this.changeDetectorRef.markForCheck();
        }
      });
  }

  private updateButtonText() {
    this.matSpinnerOptions.text =
      this.csvPatientsFormGroup.patientsToCreate.length > 0
        ? this.translocoService.translate('Create') +
          ' ' +
          this.csvPatientsFormGroup.patientsToCreate.length +
          ' ' +
          this.translocoService.translate(
            'New Patient' + (this.csvPatientsFormGroup.patientsToCreate.length === 1 ? '' : 's')
          )
        : this.translocoService.translate('Continue');
  }

  private setExistingPatients(existingPatients: boolean[]) {
    this.existingPatients = existingPatients;
    this.csvPatientsFormGroup.controls.doesPatientExist.setValue(existingPatients);
    this.changeDetectorRef.markForCheck();
  }
}
